import { API_BASE_URL, API_TIMEOUT } from "../config/constants";
import { useAuthStore } from "../store/auth-store";

/**
 * API Error class
 */
export class APIError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "APIError";
  }
}

/**
 * API Client configuration
 */
interface APIClientConfig {
  baseURL?: string;
  timeout?: number;
  headers?: Record<string, string>;
}

/**
 * Create API client with default configuration
 */
export class APIClient {
  private baseURL: string;
  private timeout: number;
  private headers: Record<string, string>;

  constructor(config: APIClientConfig = {}) {
    this.baseURL = config.baseURL || API_BASE_URL;
    this.timeout = config.timeout || API_TIMEOUT;
    this.headers = {
      "Content-Type": "application/json",
      ...config.headers,
    };
  }

  /**
   * Get current auth token from store
   * @returns JWT token or null
   */
  private getAuthToken(): string | null {
    const state = useAuthStore.getState();
    return state.token;
  }

  /**
   * Handle 401 Unauthorized responses
   * Clears auth state and triggers re-authentication flow
   */
  private handle401Unauthorized(): void {
    const { clearAuth } = useAuthStore.getState();
    clearAuth();

    // Only log that auth was cleared, never log the actual token
    console.warn("Authentication expired. Please sign in again.");
  }

  /**
   * Make a request to the API
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      // Automatically include auth token if available
      const token = this.getAuthToken();
      const requestHeaders: Record<string, string> = {
        ...this.headers,
        ...((options.headers as Record<string, string>) || {}),
      };

      // Add Authorization header if token exists
      if (token) {
        requestHeaders["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        ...options,
        headers: requestHeaders,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        this.handle401Unauthorized();

        const error = await response.json().catch(() => ({}));
        throw new APIError(
          error.message || "Authentication required. Please sign in again.",
          401,
          error.code || "UNAUTHORIZED",
          error.details,
        );
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new APIError(
          error.message || "API request failed",
          response.status,
          error.code,
          error.details,
        );
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof APIError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === "AbortError") {
          throw new APIError("Request timeout", 408);
        }
        throw new APIError(error.message);
      }

      throw new APIError("Unknown error occurred");
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    const searchParams = params
      ? `?${new URLSearchParams(params).toString()}`
      : "";
    return this.request<T>(`${endpoint}${searchParams}`, {
      method: "GET",
    });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: "DELETE",
    });
  }
}

/**
 * Default API client instance
 */
export const apiClient = new APIClient();
